import { cn } from '@/lib/utils';
import { getSeptimaLogoPath } from '@/lib/logos';
import { ReactNode, forwardRef, type CSSProperties } from 'react';
import Image from '@/lib/Image';

interface SidebarProps {
    brandContent?: ReactNode;
    children: ReactNode;
    userCard?: ReactNode;
    className?: string;
    style?: CSSProperties;
    hideBrand?: boolean;
    // When true, also render the sidebar as a fixed-position drawer below lg
    // (used for the mobile slide-in). Undefined means desktop-only.
    mobileOpen?: boolean;
}

function SeptimaBrand() {
    return (
        <div className="flex items-center gap-3">
            <Image
                src={getSeptimaLogoPath()}
                alt="Septima"
                width={28}
                height={28}
                className="dark:invert"
            />
            <span className="text-body-medium font-medium">Septima</span>
        </div>
    );
}

const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
    { brandContent, children, userCard, className, style, hideBrand = false, mobileOpen },
    ref,
) {
    const showBrand = !hideBrand;

    return (
        <aside
            ref={ref}
            style={style}
            className={cn(
                'w-[264px] h-full flex-col bg-[rgb(var(--color-surface-secondary))] border-r border-[rgb(var(--color-divider))]',
                'shadow-[inset_-1px_0_0_rgba(255,255,255,0.74)]',
                // Desktop: always visible inline
                'lg:flex lg:static lg:z-auto lg:shadow-none',
                // Mobile: only visible when explicitly opened, as fixed-position drawer
                mobileOpen ? 'flex fixed inset-y-0 left-0 z-50 shadow-2xl' : 'hidden',
                className,
            )}
        >
            {/* No hairline under the brand/role-selector block — a full-width
                border here clashed with the page header's own divider. The
                role switcher is separated from the nav by whitespace instead. */}
            {showBrand && <div className="px-3 pt-3 pb-2">{brandContent || <SeptimaBrand />}</div>}

            <nav className="flex-1 overflow-y-auto px-2.5 pb-8 pt-2 scrollbar-thin">{children}</nav>

            {userCard && (
                <div className="px-3 py-3 border-t border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface)/0.7)]">
                    {userCard}
                </div>
            )}
        </aside>
    );
});

export default Sidebar;
